/**
 * Unified marketing / product-analytics tag layer.
 *
 * One place that loads and feeds every third-party measurement tool we use:
 *
 *   - Google Tag Manager       REACT_APP_GTM_ID            (GTM-XXXXXXX)
 *   - Google Analytics 4 and Google Ads tags are managed through GTM.
 *   - Meta pixel (optional)    REACT_APP_FACEBOOK_PIXEL_ID
 *   - PostHog                  REACT_APP_POSTHOG_KEY / REACT_APP_POSTHOG_HOST
 *
 * Everything is optional: with no IDs configured nothing is loaded and every
 * function is a silent no-op, so local development and forks stay untracked.
 *
 * Consent rules (GDPR / Kenya DPA):
 *   analytics consent -> GTM, PostHog
 *   marketing consent -> GTM, Meta advertising signals
 * Google Consent Mode v2 defaults are set to "denied" before any tag loads and
 * updated the moment the visitor answers the cookie banner.
 */

import { getConsent, CONSENT_EVENT } from "./cookieConsent";
import { ANALYTICS_IDS } from "../config/analyticsIds";

const env = (key) => {
  const configured = process.env[key];
  const value = configured === undefined ? ANALYTICS_IDS[key] : configured;
  return value && String(value).trim() ? String(value).trim() : "";
};

export const TAG_CONFIG = {
  gtmId: env("REACT_APP_GTM_ID"),
  gaPropertyId: env("REACT_APP_GA_PROPERTY_ID"),
  facebookPixelId: env("REACT_APP_FACEBOOK_PIXEL_ID"),
  posthogKey: env("REACT_APP_POSTHOG_KEY"),
  posthogHost: env("REACT_APP_POSTHOG_HOST") || "https://us.i.posthog.com",
  ahrefsKey: env("REACT_APP_AHREFS_KEY"),
  clarityProjectId: env("REACT_APP_CLARITY_PROJECT_ID"),
};

/** Which tools are configured — used by the admin "tag health" panel. */
export function getTagStatus() {
  return [
    { key: "gtm", name: "Google Tag Manager", id: TAG_CONFIG.gtmId, needs: "REACT_APP_GTM_ID" },
    {
      key: "ga4",
      name: "Google Analytics 4 (via GTM)",
      id: TAG_CONFIG.gaPropertyId ? `Property ${TAG_CONFIG.gaPropertyId}; verify its Google tag and G-… web stream ID in GTM ${TAG_CONFIG.gtmId || ""}` : "",
      needs: "REACT_APP_GA_PROPERTY_ID",
      active: Boolean(TAG_CONFIG.gaPropertyId && TAG_CONFIG.gtmId),
    },
    { key: "meta", name: "Meta pixel", id: TAG_CONFIG.facebookPixelId, needs: "REACT_APP_FACEBOOK_PIXEL_ID" },
    { key: "posthog", name: "PostHog", id: TAG_CONFIG.posthogKey ? "project configured" : "", needs: "REACT_APP_POSTHOG_KEY" },
    { key: "ahrefs", name: "Ahrefs Analytics", id: TAG_CONFIG.ahrefsKey, needs: "REACT_APP_AHREFS_KEY" },
    { key: "clarity", name: "Microsoft Clarity", id: TAG_CONFIG.clarityProjectId, needs: "REACT_APP_CLARITY_PROJECT_ID" },
  ].map((tag) => ({ ...tag, active: tag.active === undefined ? Boolean(tag.id) : tag.active }));
}

function consentState() {
  const consent = getConsent();
  return {
    analytics: Boolean(consent && consent.analytics),
    marketing: Boolean(consent && consent.marketing),
  };
}

// ------------------------------------------------------------------
// Consent commands share GTM's dataLayer; the standalone gtag.js tag is not loaded.
// ------------------------------------------------------------------

function ensureGtag() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

let consentDefaultsSet = false;
function setConsentDefaults() {
  if (consentDefaultsSet) return;
  const gtag = ensureGtag();
  if (!gtag) return;
  consentDefaultsSet = true;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
}

function pushConsentUpdate() {
  const gtag = ensureGtag();
  if (!gtag) return;
  const { analytics, marketing } = consentState();
  gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });
}

function injectScript(id, src, attrs = {}) {
  if (typeof document === "undefined") return null;
  if (document.getElementById(id)) return document.getElementById(id);
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
  document.head.appendChild(script);
  return script;
}

// ------------------------------------------------------------------
// Individual loaders
// ------------------------------------------------------------------

let metaLoaded = false;
function loadMetaPixel() {
  if (metaLoaded || !TAG_CONFIG.facebookPixelId || typeof document === "undefined") return;
  metaLoaded = true;
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = true; t.id = "meta-pixel-script"; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", TAG_CONFIG.facebookPixelId);
}

let posthogLoaded = false;
function loadPostHog() {
  if (posthogLoaded || !TAG_CONFIG.posthogKey || typeof window === "undefined") return;
  posthogLoaded = true;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r; e.__SV || ((window.posthog = e), (e._i = []), (e.init = function (i, s, a) {
      function g(t, e) { var o = e.split("."); 2 == o.length && ((t = t[o[0]]), (e = o[1])); t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; }
      ((p = t.createElement("script")).type = "text/javascript"), (p.async = !0), (p.id = "posthog-script"), (p.src = s.api_host + "/static/array.js");
      (r = t.getElementsByTagName("script")[0])
        ? r.parentNode.insertBefore(p, r)
        : (t.head || t.documentElement).appendChild(p);
      var u = e; for (void 0 !== a ? (u = e[a] = []) : (a = "posthog"), u.people = u.people || [], u.toString = function (t) { var e = "posthog"; return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e; }, u.people.toString = function () { return u.toString(1) + ".people (stub)"; }, o = "capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys".split(" "), n = 0; n < o.length; n++) g(u, o[n]);
      e._i.push([i, s, a]);
    }), (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */
  window.posthog.init(TAG_CONFIG.posthogKey, {
    api_host: TAG_CONFIG.posthogHost,
    capture_pageview: false,
    persistence: "localStorage+cookie",
    autocapture: false,
    person_profiles: "identified_only",
  });
}

let ahrefsLoaded = false;
function loadAhrefs() {
  if (ahrefsLoaded || !TAG_CONFIG.ahrefsKey || typeof document === "undefined") return;
  ahrefsLoaded = true;
  injectScript("ahrefs-analytics-script", "https://analytics.ahrefs.com/analytics.js", {
    "data-key": TAG_CONFIG.ahrefsKey,
  });
}

function updateToolConsent({ analytics, marketing }) {
  if (window.posthog) {
    if (analytics && window.posthog.opt_in_capturing) window.posthog.opt_in_capturing();
    if (!analytics && window.posthog.opt_out_capturing) window.posthog.opt_out_capturing();
  }
  if (window.fbq) window.fbq("consent", marketing ? "grant" : "revoke");
}

// ------------------------------------------------------------------
// Public API
// ------------------------------------------------------------------

let initialised = false;

/** Loads every configured tag that current consent allows. Safe to call often. */
export function initMarketingTags() {
  if (typeof window === "undefined") return;
  setConsentDefaults();

  if (!initialised) {
    initialised = true;
    window.addEventListener(CONSENT_EVENT, () => {
      pushConsentUpdate();
      updateToolConsent(consentState());
      initMarketingTags();
    });
  }

  const { analytics, marketing } = consentState();
  pushConsentUpdate();
  updateToolConsent({ analytics, marketing });

  if (analytics) {
    loadPostHog();
    loadAhrefs();
  }
  if (marketing) {
    loadMetaPixel();
  }
}

/** Page view for GTM and PostHog (SPA route changes included). */
export function tagPageView(path, title) {
  if (typeof window === "undefined") return;
  const { analytics, marketing } = consentState();
  if (!analytics && !marketing) return;
  initMarketingTags();
  const pagePath = path || window.location.pathname + window.location.search;
  const pageTitle = title || document.title;

  if (analytics && window.dataLayer) {
    window.dataLayer.push({
      event: "page_view",
      page_path: pagePath,
      page_location: window.location.origin + pagePath,
      page_referrer: document.referrer || "",
      page_title: pageTitle,
    });
  }
  if (analytics && window.posthog && window.posthog.capture) {
    window.posthog.capture("$pageview", { $current_url: window.location.href, path: pagePath, title: pageTitle });
  }
  if (marketing && window.fbq) window.fbq("track", "PageView");
}

/** Any custom business event — mirrored to GA4, GTM, PostHog and Meta. */
export function tagEvent(name, params = {}) {
  if (typeof window === "undefined" || !name) return;
  const { analytics, marketing } = consentState();
  if (!analytics && !marketing) return;
  initMarketingTags();
  const payload = { ...params };

  if (analytics && window.dataLayer) window.dataLayer.push({ event: name, ...payload });
  if (analytics && window.posthog && window.posthog.capture) window.posthog.capture(name, payload);
  if (marketing && window.fbq) window.fbq("trackCustom", name, payload);
}

/** Fires the Google Ads conversion + Meta Lead when an enquiry is submitted. */
export function tagEnquiryConversion(params = {}) {
  if (typeof window === "undefined") return;
  const { marketing } = consentState();
  tagEvent("generate_lead", { currency: "KES", value: 1, ...params });
  if (marketing && window.dataLayer) {
    window.dataLayer.push({ event: "google_ads_conversion", currency: "KES", value: 1, ...params });
  }
  if (marketing && window.fbq) window.fbq("track", "Lead");
}

/** Attaches a stable visitor id so PostHog sessions stitch together. */
export function identifyVisitor(deviceId, traits = {}) {
  if (typeof window === "undefined" || !deviceId) return;
  if (!consentState().analytics) return;
  initMarketingTags();
  if (window.posthog && window.posthog.identify) window.posthog.identify(deviceId, traits);
}
